/**
 * The WebSocket protocol of docs/CONTRACT.md §5. `wss://<api-host>/api/ws?token=<deviceToken>`.
 *
 * Server -> client: ready, message, message.edited, message.deleted, typing, presence, read,
 * call.ring, call.end, pong.
 * Client -> server: message.send, typing, read, ping.
 *
 * REST `POST /api/chats/:id/messages` and WS `message.send` are the same code path; the resulting
 * `message` event is broadcast to every member's live sockets, including the sender.
 */
import type {
  MessageReactionView,
  PollView, CallView, MessageKind, MessageView, UserView } from "./types";

/* ---------------------------------------------------------------- close codes */

/** §5: no valid device token. Terminal — reconnecting cannot fix an invalid token. */
export const WS_UNAUTHORIZED = 4401;
/** §11: a valid token whose account is not `approved` (at upgrade, or when suspended mid-session). */
export const WS_NOT_APPROVED = 4403;

/* --------------------------------------------------------- server -> client */

export type WsReady = {
  t: "ready";
  userId: string;
  /** Every userId currently connected. */
  online: string[];
};

export type WsMessage = { t: "message"; chatId: string; message: MessageView };
export type WsMessageEdited = { t: "message.edited"; chatId: string; message: MessageView };
export type WsMessageDeleted = { t: "message.deleted"; chatId: string; messageId: string };
/**
 * W9: a message's reactions changed. The whole aggregated list travels, not a delta — the same
 * decision `message.edited` made, so a client applies it without ordering rules of its own.
 */
/** W13: a chat's disappearing-message timer changed; `seconds: null` is off. */
export type WsChatDisappearing = { t: "chat.disappearing"; chatId: string; seconds: number | null };
/** W13: messages passed their chat's timer and are gone — a hard delete, not a tombstone. */
export type WsMessagesExpired = { t: "messages.expired"; chatId: string; messageIds: string[] };

/** W11: a poll's tallies changed; the whole view travels, like an edit's or a reaction's. */
export type WsMessagePoll = {
  t: "message.poll";
  chatId: string;
  messageId: string;
  poll: PollView;
};

export type WsMessageReaction = {
  t: "message.reaction";
  chatId: string;
  messageId: string;
  reactions: MessageReactionView[];
};
/** W5: the chat's pinned message changed; `message: null` means the bar comes down. */
export type WsChatPinned = { t: "chat.pinned"; chatId: string; message: MessageView | null };
/**
 * W7: this user's **other** live devices should stop ringing — one of them answered or declined.
 * Sent to the acting user's own sockets, never to the chat: the person who acted is the only one
 * whose other devices need telling, and the chat already hears the join or the decline.
 */
export type WsCallDismiss = {
  t: "call.dismiss";
  chatId: string;
  callId: string;
  reason: "answered" | "declined";
};

export type WsTyping = {
  t: "typing";
  chatId: string;
  userId: string;
  /** Epoch ms until which the indicator is valid. */
  until: number;
};

export type WsPresence = {
  t: "presence";
  userId: string;
  online: boolean;
  lastSeenAt: number | null;
};

export type WsRead = { t: "read"; chatId: string; userId: string; messageId: string };
export type WsCallRing = { t: "call.ring"; chatId: string; call: CallView; caller: UserView };
/**
 * `call.end` is published for both a decline and an end. `by` is the userId that did it, so a
 * client can tell its own echo from a peer's and stop inferring it from the participant count.
 * `declined` leaves the call live (a group call carries on); `ended` closes it.
 */
export type WsCallEnd = {
  t: "call.end";
  chatId: string;
  callId: string;
  by: string;
  reason: "declined" | "ended";
};
export type WsPong = { t: "pong"; ts: number };

export type WsServerMessage =
  | WsReady
  | WsMessage
  | WsMessageEdited
  | WsMessageDeleted
  | WsMessageReaction
  | WsMessagePoll
  | WsChatDisappearing
  | WsMessagesExpired
  | WsChatPinned
  | WsCallDismiss
  | WsTyping
  | WsPresence
  | WsRead
  | WsCallRing
  | WsCallEnd
  | WsPong;

/* --------------------------------------------------------- client -> server */

export type WsMessageSend = {
  t: "message.send";
  chatId: string;
  clientId: string;
  kind: MessageKind;
  body: string | null;
  mediaId: string | null;
  replyToId: string | null;
};

export type WsTypingSend = { t: "typing"; chatId: string };
export type WsReadSend = { t: "read"; chatId: string; messageId: string };
export type WsPing = { t: "ping" };

export type WsClientMessage = WsMessageSend | WsTypingSend | WsReadSend | WsPing;

export type WsServerMessageType = WsServerMessage["t"];
export type WsClientMessageType = WsClientMessage["t"];

const MESSAGE_KINDS: Record<MessageKind, true> = {
  text: true,
  image: true,
  video: true,
  file: true,
  voice: true,
  call: true,
  system: true,
  poll: true,
};

const SERVER_MESSAGE_TYPES: Record<WsServerMessageType, true> = {
  ready: true,
  message: true,
  "message.edited": true,
  "message.deleted": true,
  "message.reaction": true,
  "message.poll": true,
  "chat.disappearing": true,
  "messages.expired": true,
  "chat.pinned": true,
  "call.dismiss": true,
  typing: true,
  presence: true,
  read: true,
  "call.ring": true,
  "call.end": true,
  pong: true,
};

/** Type guard for the `kind` field of a `message.send` frame. */
const isMessageKind = (value: unknown): value is MessageKind =>
  typeof value === "string" && value in MESSAGE_KINDS;

/** Parses a JSON payload that must be a plain object; null for anything else. */
function parseJsonObject(raw: string): object | null {
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof value !== "object" || value === null || Array.isArray(value)) return null;
  return value;
}

/**
 * Validates an inbound frame down to the fields the server acts on. Returns null for anything
 * unknown or malformed so the caller can drop the frame.
 */
export function parseClientMessage(raw: string): WsClientMessage | null {
  const parsed = parseJsonObject(raw);
  if (!parsed) return null;
  if (!("t" in parsed) || typeof parsed.t !== "string") return null;

  const t = parsed.t;
  if (t === "ping") return { t: "ping" };

  if (!("chatId" in parsed) || typeof parsed.chatId !== "string" || parsed.chatId.length === 0) {
    return null;
  }
  const chatId = parsed.chatId;
  if (t === "typing") return { t: "typing", chatId };

  if (t === "read") {
    if (!("messageId" in parsed) || typeof parsed.messageId !== "string") return null;
    return { t: "read", chatId, messageId: parsed.messageId };
  }

  if (t !== "message.send") return null;
  if (!("clientId" in parsed) || typeof parsed.clientId !== "string") return null;
  if (!("kind" in parsed) || !isMessageKind(parsed.kind)) return null;

  return {
    t: "message.send",
    chatId,
    clientId: parsed.clientId,
    kind: parsed.kind,
    body: "body" in parsed && typeof parsed.body === "string" ? parsed.body : null,
    mediaId: "mediaId" in parsed && typeof parsed.mediaId === "string" ? parsed.mediaId : null,
    replyToId: "replyToId" in parsed && typeof parsed.replyToId === "string" ? parsed.replyToId : null,
  };
}

/**
 * Narrows a server frame to the protocol union. The discriminant and the container field every
 * handler reads are checked here; the rest is trusted server output on an authenticated socket.
 * Returns null for anything else, leaving the connection open.
 */
export function parseServerMessage(raw: unknown): WsServerMessage | null {
  if (typeof raw !== "string") return null;
  const parsed = parseJsonObject(raw);
  if (!parsed) return null;
  if (!("t" in parsed) || typeof parsed.t !== "string") return null;
  if (!(parsed.t in SERVER_MESSAGE_TYPES)) return null;
  if ("chatId" in parsed && typeof parsed.chatId !== "string") return null;
  const frame: WsServerMessage = parsed as WsServerMessage;
  return frame;
}
